import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import ArticleManagementTable from "@/components/admin/ArticleManagementTable";
import UserManagementTable from "@/components/admin/UserManagementTable";
import SiteSettingsForm from "@/components/admin/SiteSettingsForm";
import { VapidKeyManagement } from "@/components/admin/VapidKeyManagement";
import { Article } from "@/types/article";
import { useNavigate } from "react-router-dom";
import { useUserManagement } from "@/hooks/useUserManagement";

type User = {
  id: number | string;
  name: string;
  email: string;
  role: string;
  subscription: string;
  joinDate: string;
  lastActive: string;
};

interface DashboardTabsProps {
  articles: Article[];
  filteredArticles: Article[];
  archivedArticles: Article[];
  filteredArchivedArticles: Article[];
  loadingArticles: boolean;
  deleteDialog: { open: boolean; article: Article | null };
  onView: (id: number | string) => void;
  onEdit: (id: number | string) => void;
  onOpenDelete: (article: Article) => void;
  onCloseDelete: () => void;
  onDelete: (id: number | string) => void;
  onArchive: (id: number | string) => void;
  onUnarchive: (id: number | string) => void;
  searchTerm: string;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  filteredUsers: User[];
}

const DashboardTabs = (props: DashboardTabsProps) => {
  const navigate = useNavigate();
  const { users, isLoading: loadingUsers, handleDeleteUser, handleEditUser } = useUserManagement();

  return (
    <Tabs defaultValue="articles">
      <TabsList className="mb-6">
        <TabsTrigger value="articles">Content Management</TabsTrigger>
        <TabsTrigger value="archived">Archived</TabsTrigger>
        <TabsTrigger value="users">User Management</TabsTrigger>
        <TabsTrigger value="settings">Site Settings</TabsTrigger>
        <TabsTrigger value="notifications">Notifications</TabsTrigger>
      </TabsList>
      <TabsContent value="articles">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Articles Management</CardTitle>
              <CardDescription>Manage all published and draft articles</CardDescription>
            </div>
            <Button
              className="bg-ai-indigo hover:bg-ai-indigo/90"
              onClick={() => navigate("/admin/create-article")}
            >
              <Plus className="mr-2 h-4 w-4" /> New Article
            </Button>
          </CardHeader>
          <CardContent>
            <ArticleManagementTable
              articles={props.articles}
              filteredArticles={props.filteredArticles}
              loading={props.loadingArticles}
              deleteDialog={props.deleteDialog}
              onView={props.onView}
              onEdit={props.onEdit}
              onOpenDelete={props.onOpenDelete}
              onCloseDelete={props.onCloseDelete}
              onDelete={props.onDelete}
              searchTerm={props.searchTerm}
              onSearch={props.onSearch}
              onArchive={props.onArchive}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline">Previous</Button>
            <Button>Next</Button>
          </CardFooter>
        </Card>
      </TabsContent>
      <TabsContent value="archived">
        <Card>
          <CardHeader>
            <CardTitle>Archived Articles</CardTitle>
            <CardDescription>Soft-deleted or archived articles</CardDescription>
          </CardHeader>
          <CardContent>
            <ArticleManagementTable
              articles={props.archivedArticles}
              filteredArticles={props.filteredArchivedArticles}
              loading={props.loadingArticles}
              deleteDialog={props.deleteDialog}
              onView={props.onView}
              onEdit={props.onEdit}
              onOpenDelete={props.onOpenDelete}
              onCloseDelete={props.onCloseDelete}
              onDelete={props.onDelete}
              onArchive={props.onArchive}
              onUnarchive={props.onUnarchive}
              searchTerm={props.searchTerm}
              onSearch={props.onSearch}
              archiveMode
            />
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="users">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>User Management</CardTitle>
              <CardDescription>Manage users and their subscription status</CardDescription>
            </div>
            <Button className="bg-ai-indigo hover:bg-ai-indigo/90">
              <Plus className="mr-2 h-4 w-4" /> Add User
            </Button>
          </CardHeader>
          <CardContent>
            <UserManagementTable 
              users={users || []} 
              isLoading={loadingUsers}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline">Previous</Button>
            <Button>Next</Button>
          </CardFooter>
        </Card>
      </TabsContent>
      <TabsContent value="settings">
        <Card>
          <CardHeader>
            <CardTitle>Site Settings</CardTitle>
            <CardDescription>Manage global site configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <SiteSettingsForm />
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="notifications">
        <VapidKeyManagement />
      </TabsContent>
    </Tabs>
  );
};

export default DashboardTabs;
